import { UserRoles } from "../config/routes";
import {
  canUseDevOverrides,
  getDevRoleOverride,
  getDevToolsOverride,
  getOverrideStatus,
  isDevelopmentEnvironment,
  isLocalhost,
} from "./devOverrides";

const DEV_PREVIEW_ROLE_KEY = "orion:dev-preview-role";
const DEV_PREVIEW_EMAIL_KEY = "orion:dev-preview-email";
export const DEV_PREVIEW_ROLE_EVENT = "orion:dev-preview-role-change";

/** Parse `roles.tools`, which may be an array (new) or a JSON string (legacy). */
export const parseUserTools = (tools: unknown): string[] => {
  if (Array.isArray(tools)) return tools.map(String);
  if (typeof tools === "string") {
    try {
      const parsed = JSON.parse(tools);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch (error) {
      console.error("Failed to parse user tools:", tools, error);
      return [];
    }
  }
  return [];
};

/** Localhost-only "preview as role" switch used to review role-specific UI. */
export const getDevPreviewRole = (email?: string | null): string | null => {
  if (!canUseDevOverrides(email ?? null)) return null;
  try {
    return sessionStorage.getItem(DEV_PREVIEW_ROLE_KEY);
  } catch {
    return null;
  }
};

export const setDevPreviewRole = (role: string | null): void => {
  try {
    if (role) sessionStorage.setItem(DEV_PREVIEW_ROLE_KEY, role);
    else sessionStorage.removeItem(DEV_PREVIEW_ROLE_KEY);
  } catch {
    // sessionStorage unavailable; preview stays off
  }
  window.dispatchEvent(new Event(DEV_PREVIEW_ROLE_EVENT));
};

/** Localhost-only "view as this channel manager" email (home page preview). */
export const getDevPreviewEmail = (email?: string | null): string | null => {
  if (!canUseDevOverrides(email ?? null)) return null;
  try {
    return sessionStorage.getItem(DEV_PREVIEW_EMAIL_KEY);
  } catch {
    return null;
  }
};

/** Preview email to send as `asEmail` (dev builds on localhost only). */
export const devPreviewEmailForRequests = (): string | undefined => {
  if (!isDevelopmentEnvironment() || !isLocalhost()) return undefined;
  try {
    return sessionStorage.getItem(DEV_PREVIEW_EMAIL_KEY) || undefined;
  } catch {
    return undefined;
  }
};

export const setDevPreviewEmail =(previewEmail: string | null): void => {
  try {
    if (previewEmail) sessionStorage.setItem(DEV_PREVIEW_EMAIL_KEY, previewEmail);
    else sessionStorage.removeItem(DEV_PREVIEW_EMAIL_KEY);
  } catch {
    // sessionStorage unavailable; preview stays off
  }
  window.dispatchEvent(new Event(DEV_PREVIEW_ROLE_EVENT));
};

/**
 * Role + tools for the signed-in user, applying (in order) the real claims,
 * env-based dev overrides, then the localhost preview role.
 */
export const resolveUserRoles = (user: any): UserRoles => {
  const email: string | null = user?.email ?? null;
  let role: string | undefined = user?.roles?.role || undefined;
  let tools = parseUserTools(user?.roles?.tools);

  if (canUseDevOverrides(email)) {
    const roleOverride = getDevRoleOverride();
    const toolsOverride = getDevToolsOverride();
    if (roleOverride || toolsOverride.length > 0) {
      console.warn("🔧 DEV OVERRIDE ACTIVE", getOverrideStatus(email));
      role = roleOverride || role;
      if (toolsOverride.length > 0) tools = toolsOverride;
    }
    const previewRole = getDevPreviewRole(email);
    if (previewRole) role = previewRole;
  }

  return { role, tools };
};
